import { fabric } from 'fabric';
import type { MutableRefObject } from 'react';
import Vec2 from 'vec2';

import { DrawingShape } from '~/core/models';
import { isEqApplianceId } from '~/core/utils';
import { findImageByName } from '~/core/utils/drawingTool/drawingTool.utils';

/**
 * Finds the shape that contains a given appliance by its canvas object name.
 */
export const findShapeContainingAppliance = (
  shapes: DrawingShape[],
  applianceName: string
): DrawingShape | undefined =>
  shapes.find((shape) => shape.appliances.some(isEqApplianceId(applianceName)));

/**
 * Result of searching for an appliance object on canvas.
 */
export interface ApplianceSearchResult {
  /** Appliance found directly on canvas (already extracted) */
  applianceOnCanvas: fabric.Image | undefined;
  /** Appliance found inside shape's fabricGroup (needs extraction) */
  applianceInGroup: fabric.Image | undefined;
  /** The appliance object (either location) */
  appliance: fabric.Image | undefined;
}

/**
 * Finds an appliance object either on the canvas or inside a shape's fabricGroup.
 * Search order: canvas first, then fabricGroup (matches original behavior).
 */
export const findApplianceObject = (
  canvas: fabric.Canvas,
  shape: DrawingShape,
  applianceName: string
): ApplianceSearchResult => {
  const applianceOnCanvas = findImageByName(canvas.getObjects(), applianceName);

  let applianceInGroup: fabric.Image | undefined;

  if (!applianceOnCanvas && shape.fabricGroup) {
    applianceInGroup = findImageByName(
      shape.fabricGroup.getObjects(),
      applianceName
    );
  }

  return {
    applianceOnCanvas,
    applianceInGroup,
    appliance: applianceOnCanvas || applianceInGroup,
  };
};

/**
 * Checks if the appliance is already selected and on canvas.
 * This detects when handleApplianceInteraction already processed this click.
 */
export const isApplianceAlreadySelected = (
  applianceOnCanvas: fabric.Image | undefined,
  activeApplianceId: string | undefined,
  targetName: string
): boolean => Boolean(applianceOnCanvas) && activeApplianceId === targetName;

/**
 * Moves an appliance from its fabricGroup to the canvas root and selects it.
 * NOTE: Does NOT move previous appliance back to group (per architectural comment).
 */
export const moveApplianceToCanvasAndSelect = (
  canvas: fabric.Canvas,
  shape: DrawingShape,
  searchResult: ApplianceSearchResult
): void => {
  const { appliance, applianceInGroup } = searchResult;

  if (!appliance) {
    return;
  }

  // If appliance was in group, remove it first
  if (applianceInGroup && shape.fabricGroup) {
    shape.fabricGroup.remove(appliance);
  }

  // Add to canvas root and select
  canvas.add(appliance);
  canvas.setActiveObject(appliance);
};

/**
 * Parameters for setting up appliance movement tracking.
 */
export interface MovementSetupParams {
  movingItemRef: MutableRefObject<DrawingShape | string | undefined>;
  shapeStartDragRef: MutableRefObject<Vec2>;
  targetName: string;
  pointer: Vec2;
}

/**
 * Sets up refs for tracking appliance movement during drag.
 * These refs are read by onMouseMove and onMouseUp handlers.
 */
export const setupApplianceMovement = ({
  movingItemRef,
  shapeStartDragRef,
  targetName,
  pointer,
}: MovementSetupParams): void => {
  movingItemRef.current = targetName;
  shapeStartDragRef.current = pointer;
};
